<template>
	<div class="app-lists">
		<div class="app-lists__add">
			<app-resizable-dropdown
				ref="$dropdown"
				position="left"
				:resizable="false"
				:initial-width="320"
				:initial-height="436"
				:close-by-click-outside="false"
				:content-z-index="10003"
				:mobile-title="$t('file_info_lists')"
				name="$appLists"
			>
				<template #activator>
					<app-tooltip :text="$t('add_to_list')">
						<template #activator>
							<app-icon
								class="app-lists__add-btn"
								name="plus"
								width="14px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<app-list-select-dropdown
					:segment="segment"
					:element-id="elementId"
					:added-lists="modelValue"
					show-close-icon
					@close="close"
					@add-list="refreshLists"
				/>
			</app-resizable-dropdown>
			<div class="app-lists__add-text">{{ $t("file_info_lists") }}:</div>
		</div>
		<div class="app-lists__items">
			<div
				v-for="list in modelValue"
				:key="list.Id"
				class="app-lists__items-item"
			>
				<div class="app-lists__items-item-name">{{ list.Name }}</div>
				<app-icon
					class="app-lists__items-item-delete"
					name="close"
					width="12px"
					color="#D2D2D2"
					hover-color="#499259"
					cursor="pointer"
					@click="onDelete(list)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppListSelectDropdown from "./AppListSelectDropdown.vue";
import { AnalyticsApi } from "@/ api/AnalyticsApi";
import type { AnalyticsListItem } from "@/store/clients";

const props = defineProps<{
	modelValue: AnalyticsListItem[];
	segment: number;
	elementId: number;
}>();

const emit = defineEmits<{
	(e: "update:modelValue", value: AnalyticsListItem[]): void;
}>();

const $dropdown = ref<typeof AppResizableDropdown | null>(null);

const close = () => {
	$dropdown.value?.close();
};

const refreshLists = async () => {
	try {
		const res = await AnalyticsApi.getListOfElement(props.elementId, props.segment);
		emit("update:modelValue", res?.Lists ?? []);
	} catch (e) {
		console.error(e);
	}
};

const onDelete = async (list: AnalyticsListItem) => {
	try {
		await AnalyticsApi.removeElementIntoList(list.Id, props.segment, props.elementId);
		emit(
			"update:modelValue",
			props.modelValue.filter((l) => l.Id !== list.Id),
		);
	} catch (e) {
		console.error(e);
	}
};

defineExpose({ close });
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.app-lists {
	display: flex;
	flex-direction: column;

	&__add {
		display: flex;
		align-items: center;
		gap: 6px;

		&-text {
			font-size: 14px;
			line-height: 18px;
			font-weight: 500;
			color: $color-text_main;
		}

		&-btn {
			margin-top: -2px;
		}
	}

	&__items {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 2px;
		max-width: 290px;
		margin-top: 4px;

		&-item {
			display: inline;
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;

			&-name {
				display: inline;
				font-size: 14px;
				line-height: 18px;
			}

			&-delete {
				margin-left: 2px;
			}
		}
	}
}
</style>
